/**
 * CopyProviderSettingsDialog — selective org→org AI-provider replication
 * (ai-docs/lexportal-copy-provider-settings-plan.md §4.4). Platform-admin only.
 *
 * Copies SELECTED categories (api-keys / oauth / openrouter / aliases /
 * chat-default) from a SOURCE org to a DEST org. Category checkboxes reflect
 * what the SOURCE actually has (absent → disabled); an overwrite warning lists
 * what the DEST already holds for the selected categories; a typed-confirm
 * (dest slug) guards the overwrite. After a successful copy, an "Undo last
 * copy" affordance calls `revert-copy` (D-5 snapshot restore).
 *
 * Reached from two entry points, both opening this dialog:
 *  - the Organizations row action ("Copy AI settings", source preselected)
 *  - the LLM Providers page ("Copy settings…", current org preselected source)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Copy, Loader2, Undo2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import type { Organization } from "@/types/platform";
import type { CopyCategory } from "@/types/llm-providers";

type CategorySummary = Record<CopyCategory, { present: boolean; detail?: string }>;

const CATEGORY_META: ReadonlyArray<{ key: CopyCategory; label: string; note?: string }> = [
  { key: "api-keys", label: "API-key credentials" },
  {
    key: "oauth",
    label: "ChatGPT-subscription OAuth",
    note: "shared refresh chain — safe within this environment",
  },
  { key: "openrouter", label: "OpenRouter / custom endpoints" },
  { key: "aliases", label: "Model aliases (tier ladder)" },
  { key: "chat-default", label: "Chat default" },
];

export interface CopyProviderSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selectable orgs (typically the ACTIVE set). */
  orgs: Organization[];
  /** Preselect the current org as the SOURCE (Organizations row / LLM Providers page). */
  initialSourceId?: string;
  /** Preselect a DEST (rare — e.g. opened on a specific dest row). */
  initialDestId?: string;
  /** Called after a successful copy or revert so the caller can refresh. */
  onDone?: () => void;
}

export function CopyProviderSettingsDialog({
  open,
  onOpenChange,
  orgs,
  initialSourceId,
  initialDestId,
  onDone,
}: CopyProviderSettingsDialogProps) {
  const api = usePlatformApi();
  const [sourceId, setSourceId] = useState(initialSourceId ?? "");
  const [destId, setDestId] = useState(initialDestId ?? "");
  const [cats, setCats] = useState<Set<CopyCategory>>(new Set());
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [srcSummary, setSrcSummary] = useState<CategorySummary | null>(null);
  const [destSummary, setDestSummary] = useState<CategorySummary | null>(null);
  const [result, setResult] = useState<{ copied: CopyCategory[]; slug: string } | null>(null);

  // Reset everything on (re)open.
  useEffect(() => {
    if (open) {
      setSourceId(initialSourceId ?? "");
      setDestId(initialDestId ?? "");
      setCats(new Set());
      setConfirm("");
      setError(null);
      setResult(null);
    }
  }, [open, initialSourceId, initialDestId]);

  // Fetch the SOURCE presence summary (drives which categories are selectable).
  useEffect(() => {
    if (!open || !sourceId) {
      setSrcSummary(null);
      return;
    }
    let cancelled = false;
    api
      .getProviderSettingsSummary(sourceId)
      .then((r) => {
        if (!cancelled) setSrcSummary(r.data.summary);
      })
      .catch(() => {
        if (!cancelled) setSrcSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, open, sourceId]);

  // Fetch the DEST presence summary (drives the overwrite warning).
  useEffect(() => {
    if (!open || !destId) {
      setDestSummary(null);
      return;
    }
    let cancelled = false;
    api
      .getProviderSettingsSummary(destId)
      .then((r) => {
        if (!cancelled) setDestSummary(r.data.summary);
      })
      .catch(() => {
        if (!cancelled) setDestSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, open, destId]);

  const destSlug = useMemo(() => orgs.find((o) => o.orgId === destId)?.slug ?? "", [orgs, destId]);
  const sourceName = useMemo(() => orgs.find((o) => o.orgId === sourceId)?.name ?? "", [orgs, sourceId]);

  // If the source is changed to the current dest, clear the dest — they can
  // never be equal (the dest select filters out the source; without this the
  // stale destId would leave the confirm input pointing at a hidden option).
  useEffect(() => {
    if (sourceId && destId && sourceId === destId) setDestId("");
  }, [sourceId, destId]);

  // Prune selected categories that the source can't provide (keeps state honest
  // when the source changes under an existing selection).
  useEffect(() => {
    if (!srcSummary) return;
    setCats((prev) => {
      const next = new Set<CopyCategory>();
      for (const cat of prev) if (srcSummary[cat]?.present) next.add(cat);
      return next.size === prev.size ? prev : next;
    });
  }, [srcSummary]);

  const toggle = useCallback((cat: CopyCategory) => {
    setCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  // Overwrite warning: which SELECTED categories the DEST already has (get REPLACED).
  const overwritten = useMemo(() => {
    if (!destSummary) return [] as string[];
    return CATEGORY_META.filter((m) => cats.has(m.key) && destSummary[m.key]?.present).map(
      (m) => m.label,
    );
  }, [cats, destSummary]);

  // Advisory alias-coherence: copying aliases but NOT openrouter while the dest
  // lacks openrouter (a common footgun with the deepseek fallback pins).
  const coherenceWarning = useMemo(() => {
    if (!cats.has("aliases") || cats.has("openrouter")) return null;
    if (destSummary && !destSummary.openrouter?.present) {
      return "Copying aliases but not OpenRouter credentials — any alias that pins OpenRouter will be unavailable on the destination until you add one.";
    }
    return null;
  }, [cats, destSummary]);

  const canSubmit =
    !!sourceId && !!destId && sourceId !== destId && cats.size > 0 && confirm === destSlug && !busy;

  const doCopy = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.copyProviderSettings(destId, {
        sourceOrgId: sourceId,
        categories: [...cats],
        confirm,
      });
      setResult({ copied: r.data.copied, slug: r.data.dest.slug });
      onDone?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Copy failed");
    } finally {
      setBusy(false);
    }
  }, [api, canSubmit, cats, confirm, destId, onDone, sourceId]);

  const doRevert = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api.revertCopyProviderSettings(destId, destSlug);
      setResult(null);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Revert failed");
    } finally {
      setBusy(false);
    }
  }, [api, destId, destSlug, onDone, onOpenChange]);

  const activeOrgs = orgs.filter((o) => o.status === "ACTIVE");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg space-y-4" data-testid="copy-provider-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-foreground">
            <Copy className="h-5 w-5" />
            Copy AI Provider Settings
          </DialogTitle>
          <DialogDescription>
            Replicate selected AI-provider settings from one organization onto another.
            The destination&apos;s selected categories are <strong>replaced</strong>; others are untouched.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="copy-provider-settings-result">
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300">
              Copied <strong>{result.copied.join(", ")}</strong> onto{" "}
              <span className="font-mono">{result.slug}</span>. The destination re-resolves within ~1 minute.
            </div>
            {error && (
              <div className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical">
                {error}
              </div>
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={doRevert}
                disabled={busy}
                data-testid="copy-provider-settings-undo"
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
              >
                <Undo2 className="h-3.5 w-3.5" />
                {busy ? "Undoing…" : "Undo last copy"}
              </button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
              >
                Done
              </button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1 text-xs font-medium text-muted">
                Source org
                <select
                  value={sourceId}
                  onChange={(e) => setSourceId(e.target.value)}
                  disabled={busy}
                  data-testid="copy-source-select"
                  className="w-full rounded-md border border-border bg-panel-2 px-2 py-1.5 text-sm text-foreground"
                >
                  <option value="">Select…</option>
                  {activeOrgs.map((o) => (
                    <option key={o.orgId} value={o.orgId}>
                      {o.name} ({o.slug})
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs font-medium text-muted">
                Destination org
                <select
                  value={destId}
                  onChange={(e) => setDestId(e.target.value)}
                  disabled={busy}
                  data-testid="copy-dest-select"
                  className="w-full rounded-md border border-border bg-panel-2 px-2 py-1.5 text-sm text-foreground"
                >
                  <option value="">Select…</option>
                  {activeOrgs
                    .filter((o) => o.orgId !== sourceId)
                    .map((o) => (
                      <option key={o.orgId} value={o.orgId}>
                        {o.name} ({o.slug})
                      </option>
                    ))}
                </select>
              </label>
            </div>

            <fieldset className="space-y-1.5" data-testid="copy-categories">
              <legend className="text-xs font-medium text-muted">Settings to copy</legend>
              {CATEGORY_META.map((m) => {
                const present = srcSummary?.[m.key]?.present ?? false;
                const detail = srcSummary?.[m.key]?.detail;
                const disabled = !sourceId || !present || busy;
                return (
                  <label
                    key={m.key}
                    className={`flex items-start gap-2 rounded-md px-2 py-1 text-sm ${
                      disabled ? "opacity-45" : "hover:bg-accent/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={cats.has(m.key)}
                      disabled={disabled}
                      onChange={() => toggle(m.key)}
                      data-testid={`copy-cat-${m.key}`}
                    />
                    <span>
                      <span className="text-foreground">{m.label}</span>
                      {sourceId && (
                        <span className="ml-1 text-xs text-muted">
                          {present ? `(source: ${detail ?? "yes"})` : "(source: none)"}
                        </span>
                      )}
                      {m.note && present && (
                        <span className="block text-[11px] text-faint">ⓘ {m.note}</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </fieldset>

            {overwritten.length > 0 && (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-300"
                data-testid="copy-overwrite-warning"
              >
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                The destination already has <strong>{overwritten.join(", ")}</strong> — these will be REPLACED. Other settings stay untouched. You can Undo immediately after.
              </div>
            )}
            {coherenceWarning && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                {coherenceWarning}
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="copy-confirm" className="text-xs font-medium text-muted">
                Type the destination slug{" "}
                {destSlug && <span className="font-mono font-semibold text-foreground">{destSlug}</span>}{" "}
                to confirm:
              </label>
              <input
                id="copy-confirm"
                type="text"
                autoComplete="off"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={destSlug || "destination slug"}
                disabled={busy || !destId}
                data-testid="copy-confirm-input"
                className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
              />
            </div>

            {error && (
              <div
                className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
                data-testid="copy-provider-settings-error"
              >
                {error}
              </div>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void doCopy()}
                disabled={!canSubmit}
                data-testid="copy-provider-settings-submit"
                title={sourceName ? `Copy from ${sourceName}` : undefined}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                {busy ? "Copying…" : "Copy"}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
